import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, MessageSquare, ThumbsUp, ThumbsDown, Heart, ExternalLink, Send, CheckCircle2, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Rating } from '../ui/Rating';
import { AnimatedSection, scaleIn } from './AnimatedSection';
import { reviews, ratingBreakdown } from './homeData';
export function ReviewsSection() {
  const [reviewStep, setReviewStep] = useState<'closed' | 'ask' | 'positive' | 'negative' | 'submitted'>('closed');
  const [feedbackText, setFeedbackText] = useState('');
  const [visibleReviews, setVisibleReviews] = useState(4);
  return <AnimatedSection className="py-8 sm:py-24 bg-slate-50" data-id="element-13765">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-13766">
        <div className="text-center mb-5 sm:mb-16" data-id="element-13767">
          <h2 className="text-lg sm:text-3xl font-bold text-slate-900 mb-2 sm:mb-4" data-id="element-13768">
            Отзывы клиентов
          </h2>
          <p className="text-xs sm:text-lg text-slate-500" data-id="element-13769">
            Что говорят те, кто уже воспользовался платформой
          </p>
        </div>

        {/* Mobile: compact layout */}
        <div className="sm:hidden" data-id="element-13770">
          <div className="flex items-center gap-3 mb-4 bg-white rounded-xl p-3 border border-slate-100" data-id="element-13771">
            <div className="text-3xl font-bold text-slate-900" data-id="element-13772">4.9</div>
            <div data-id="element-13773">
              <div className="flex gap-0.5 mb-0.5" data-id="element-13774">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-13775" />)}
              </div>
              <p className="text-[10px] text-slate-500" data-id="element-13776">10,000+ отзывов</p>
            </div>
          </div>
          <div className="space-y-2.5" data-id="element-13777">
            {reviews.slice(0, visibleReviews).map(review => <div key={review.id} className="bg-white rounded-xl border border-slate-100 p-3" data-id="element-13778">
                <div className="flex items-center gap-2 mb-1" data-id="element-13779">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-100 to-slate-100 flex items-center justify-center font-bold text-indigo-600 text-[10px] flex-shrink-0" data-id="element-13780">
                    {review.name[0]}
                  </div>
                  <div className="flex-grow min-w-0" data-id="element-13781">
                    <span className="font-bold text-slate-900 text-xs" data-id="element-13782">
                      {review.name}
                    </span>
                    <span className="text-[10px] text-slate-400 ml-2" data-id="element-13783">
                      {review.date}
                    </span>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-1 ml-9" data-id="element-13784">
                  {[...Array(5)].map((_, i) => <Star key={i} className={`w-2.5 h-2.5 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-13785" />)}
                </div>
                <p className="text-xs text-slate-600 leading-relaxed ml-9" data-id="element-13786">
                  «{review.quote}»
                </p>
              </div>)}
          </div>
          {visibleReviews < reviews.length && <div className="flex justify-center mt-4" data-id="element-13787">
              <Button variant="outline" size="sm" onClick={() => setVisibleReviews(prev => Math.min(prev + 6, reviews.length))} data-id="element-13788">
                Ещё ({reviews.length - visibleReviews})
              </Button>
            </div>}
        </div>

        {/* Desktop: existing layout */}
        <div className="hidden sm:grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-start" data-id="element-13789">
          <div className="lg:col-span-4" data-id="element-13790">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={scaleIn} data-id="element-13791">
              <Card padding="lg" className="text-center border-slate-100" data-id="element-13792">
                <div className="text-6xl font-bold text-slate-900 mb-2" data-id="element-13793">
                  4.9
                </div>
                <Rating value={5} showCount={false} className="justify-center mb-2" data-id="element-13794" />
                <p className="text-sm text-slate-500 mb-8" data-id="element-13795">
                  На основе 10,000+ отзывов
                </p>
                <div className="space-y-3 mb-8" data-id="element-13796">
                  {ratingBreakdown.map(r => <div key={r.stars} className="flex items-center gap-3 text-sm" data-id="element-13797">
                      <span className="text-slate-500 w-4 text-right" data-id="element-13798">
                        {r.stars}
                      </span>
                      <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" data-id="element-13799" />
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-13800">
                        <motion.div className="h-full bg-amber-400 rounded-full" initial={{
                      width: 0
                    }} whileInView={{
                      width: `${r.percent}%`
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 0.8,
                      delay: 0.3,
                      ease: 'easeOut'
                    }} data-id="element-13801" />
                      </div>
                      <span className="text-slate-400 w-8 text-right" data-id="element-13802">
                        {r.percent}%
                      </span>
                    </div>)}
                </div>
                <Button fullWidth onClick={() => setReviewStep('ask')} data-id="element-13803">
                  <MessageSquare className="w-4 h-4 mr-2" data-id="element-13804" />
                  Оставить отзыв
                </Button>
              </Card>
            </motion.div>
          </div>
          <div className="lg:col-span-8" data-id="element-13805">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-13806">
              {reviews.slice(0, visibleReviews).map((review, idx) => <motion.div key={review.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: idx < 4 ? idx * 0.08 : 0.1
            }} data-id="element-13807">
                  <Card padding="md" className="border-slate-100 h-full" data-id="element-13808">
                    <div className="flex items-center gap-3 mb-1" data-id="element-13809">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-100 to-slate-100 flex items-center justify-center font-bold text-indigo-600 text-xs flex-shrink-0" data-id="element-13810">
                        {review.name[0]}
                      </div>
                      <div className="flex-grow min-w-0" data-id="element-13811">
                        <div className="font-bold text-slate-900 text-sm truncate" data-id="element-13812">
                          {review.name}
                        </div>
                        <div className="text-[11px] text-slate-400" data-id="element-13813">
                          {review.date}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 mb-2 ml-12" data-id="element-13814">
                      {[...Array(5)].map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-13815" />)}
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed" data-id="element-13816">
                      «{review.quote}»
                    </p>
                  </Card>
                </motion.div>)}
            </div>
            {visibleReviews < reviews.length && <div className="flex justify-center mt-8" data-id="element-13817">
                <Button variant="outline" onClick={() => setVisibleReviews(prev => Math.min(prev + 6, reviews.length))} data-id="element-13818">
                  Показать ещё ({reviews.length - visibleReviews})
                </Button>
              </div>}
          </div>
        </div>

        {/* Review Flow Modal */}
        <AnimatePresence data-id="element-13819">
          {reviewStep !== 'closed' && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => {
          setReviewStep('closed');
          setFeedbackText('');
        }} data-id="element-13820">
              <motion.div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative" initial={{
            scale: 0.9,
            opacity: 0,
            y: 20
          }} animate={{
            scale: 1,
            opacity: 1,
            y: 0
          }} exit={{
            scale: 0.9,
            opacity: 0,
            y: 20
          }} transition={{
            duration: 0.3,
            ease: [0.25, 0.46, 0.45, 0.94]
          }} onClick={(e: React.MouseEvent) => e.stopPropagation()} data-id="element-13821">
                <button onClick={() => {
              setReviewStep('closed');
              setFeedbackText('');
            }} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-13822">
                  <X className="w-5 h-5" data-id="element-13823" />
                </button>

                {reviewStep === 'ask' && <div className="text-center" data-id="element-13824">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-4 sm:mb-6" data-id="element-13825">
                      <MessageSquare className="w-6 h-6 sm:w-7 sm:h-7 text-indigo-600" data-id="element-13826" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2" data-id="element-13827">
                      Как вам наш сервис?
                    </h3>
                    <p className="text-slate-500 text-xs sm:text-sm mb-6 sm:mb-8" data-id="element-13828">
                      Ваше мнение помогает нам становиться лучше
                    </p>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-13829">
                      <button onClick={() => setReviewStep('positive')} className="flex flex-col items-center gap-2 sm:gap-3 p-4 sm:p-6 rounded-xl border-2 border-slate-100 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all duration-200 group" data-id="element-13830">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors" data-id="element-13831">
                          <ThumbsUp className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-600" data-id="element-13832" />
                        </div>
                        <span className="font-semibold text-slate-700 text-xs sm:text-sm" data-id="element-13833">
                          Всё отлично!
                        </span>
                      </button>
                      <button onClick={() => setReviewStep('negative')} className="flex flex-col items-center gap-2 sm:gap-3 p-4 sm:p-6 rounded-xl border-2 border-slate-100 hover:border-amber-300 hover:bg-amber-50/50 transition-all duration-200 group" data-id="element-13834">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors" data-id="element-13835">
                          <ThumbsDown className="w-6 h-6 sm:w-7 sm:h-7 text-amber-600" data-id="element-13836" />
                        </div>
                        <span className="font-semibold text-slate-700 text-xs sm:text-sm" data-id="element-13837">
                          Есть замечания
                        </span>
                      </button>
                    </div>
                  </div>}

                {reviewStep === 'positive' && <div className="text-center" data-id="element-13838">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4 sm:mb-6" data-id="element-13839">
                      <Heart className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-600" data-id="element-13840" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2" data-id="element-13841">
                      Спасибо! 🎉
                    </h3>
                    <p className="text-slate-500 text-xs sm:text-sm mb-6 sm:mb-8" data-id="element-13842">
                      Будем благодарны, если оставите отзыв
                    </p>
                    <div className="space-y-2.5 sm:space-y-3" data-id="element-13843">
                      <a href="https://g.page/r/YOUR_GOOGLE_ID/review" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 w-full p-3 sm:p-4 rounded-xl border-2 border-slate-100 hover:border-blue-300 hover:bg-blue-50/50 transition-all duration-200 group" data-id="element-13844">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors" data-id="element-13845">
                          <svg viewBox="0 0 24 24" className="w-4 h-4 sm:w-5 sm:h-5" fill="none" data-id="element-13846">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" data-id="element-13847" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-13848" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-13849" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-13850" />
                          </svg>
                        </div>
                        <span className="font-semibold text-slate-700 text-xs sm:text-sm" data-id="element-13851">
                          Отзыв в Google
                        </span>
                        <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 ml-auto" data-id="element-13852" />
                      </a>
                      <a href="https://yandex.ru/maps/org/YOUR_YANDEX_ID/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 w-full p-3 sm:p-4 rounded-xl border-2 border-slate-100 hover:border-red-300 hover:bg-red-50/50 transition-all duration-200 group" data-id="element-13853">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors" data-id="element-13854">
                          <svg viewBox="0 0 24 24" className="w-4 h-4 sm:w-5 sm:h-5" fill="none" data-id="element-13855">
                            <path d="M2 12C2 6.48 6.48 2 12 2s10 4.48 10 10-4.48 10-10 10S2 17.52 2 12z" fill="#FC3F1D" data-id="element-13856" />
                            <path d="M13.32 7.67h-.93c-1.49 0-2.27.75-2.27 1.86 0 1.24.54 1.82 1.65 2.54l.92.59-2.68 4.34H8.5l2.38-3.9c-1.35-.9-2.1-1.82-2.1-3.36 0-1.98 1.38-3.37 3.78-3.37h1.76v10.63h-1v-9.33z" fill="white" data-id="element-13857" />
                          </svg>
                        </div>
                        <span className="font-semibold text-slate-700 text-xs sm:text-sm" data-id="element-13858">
                          Отзыв в Яндекс
                        </span>
                        <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 ml-auto" data-id="element-13859" />
                      </a>
                    </div>
                  </div>}

                {reviewStep === 'negative' && <div data-id="element-13860">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4 sm:mb-6" data-id="element-13861">
                      <MessageSquare className="w-6 h-6 sm:w-7 sm:h-7 text-amber-600" data-id="element-13862" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 text-center" data-id="element-13863">
                      Что можно улучшить?
                    </h3>
                    <p className="text-slate-500 text-xs sm:text-sm mb-4 sm:mb-6 text-center" data-id="element-13864">
                      Мы обязательно учтём
                    </p>
                    <textarea value={feedbackText} onChange={e => setFeedbackText(e.target.value)} placeholder="Опишите вашу ситуацию..." className="w-full h-24 sm:h-32 p-3 sm:p-4 rounded-xl border border-slate-200 text-xs sm:text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none transition-all" data-id="element-13865" />
                    <Button fullWidth className="mt-3 sm:mt-4" onClick={() => {
                setReviewStep('submitted');
                setFeedbackText('');
              }} disabled={!feedbackText.trim()} data-id="element-13866">
                      <Send className="w-4 h-4 mr-2" data-id="element-13867" />
                      Отправить
                    </Button>
                  </div>}

                {reviewStep === 'submitted' && <div className="text-center" data-id="element-13868">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 200,
                damping: 15
              }} className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4 sm:mb-6" data-id="element-13869">
                      <CheckCircle2 className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-600" data-id="element-13870" />
                    </motion.div>
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-2" data-id="element-13871">
                      Спасибо!
                    </h3>
                    <p className="text-slate-500 text-xs sm:text-sm mb-4 sm:mb-6" data-id="element-13872">
                      Ваш отзыв отправлен. Мы ценим обратную связь.
                    </p>
                    <Button variant="outline" onClick={() => setReviewStep('closed')} data-id="element-13873">
                      Закрыть
                    </Button>
                  </div>}
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </AnimatedSection>;
}